import { useCallback, useRef } from 'react';

import {
  getCommonAmplitudeProperties,
  getSourceEventParams,
  useAmplitude,
} from '@crehana/analytics';
import { useIsRenderedFromWebView } from '@crehana/react-hooks/use-is-rendered-from-webview';

import type { TUserEnrollment as TOldUserEnrollment } from '@/shared/components/MyCoursesWithRules/hooks/useCourseEnrollments/types';
import { LazyMyCoursesWithFilters } from '@/shared/components/my-courses-with-filters';
import type { TUserEnrollment } from '@/shared/components/my-courses-with-filters/hooks/use-course-enrollments/types';
import {
  PageLayoutBreadcrumb,
  PageLayoutTop,
} from '@/shared/vendor/compass-web-page-layout';
import SectionTitle from '@/views/LearnDashboard/components/SectionTitle';
import { useLearnDashboardContext } from '@/views/LearnDashboard/context';
import { useCrehanaTranslation } from '@/views/LearnDashboard/hooks/useCrehanaTranslation';
import useLearnBreadcrumbTrail from '@/views/LearnDashboard/hooks/useLearnBreadcrumbTrail';

import { LEARN_DASHBOARD_PAGE_NAME } from '../../../constants';

const CoursesSection: React.FC = () => {
  const { logEvent } = useAmplitude();
  const { t } = useCrehanaTranslation();
  const { isPlayroomEnabled, me } = useLearnDashboardContext();
  const isFromApps = useIsRenderedFromWebView();
  const { items, isLoading } = useLearnBreadcrumbTrail();
  const sentAmplitudeEventRender = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  const sendAmplitudeEventRenderRedesign = useCallback(
    (courses: (TUserEnrollment | TOldUserEnrollment)[] = []) => {
      // Note: We only want to send the event once per page load. Not on every re-render, refetch, change pagination, etc.
      if (sentAmplitudeEventRender.current) {
        return;
      }

      const eventProperties: Readonly<Record<string, number | string>> = {
        ...getSourceEventParams(),
        ...getCommonAmplitudeProperties(me, isPlayroomEnabled),
        'Courses Enrolled Count': courses.length,
        'Quiz Certification Available': 'Yes',
      };

      logEvent({
        event: 'Render Home Courses Enrolled',
        data: eventProperties,
      });

      sentAmplitudeEventRender.current = true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPlayroomEnabled, me],
  );

  return (
    <>
      {!isFromApps && (
        <PageLayoutTop className="tw-pb-0" transparent>
          <PageLayoutBreadcrumb items={items} isLoading={isLoading} />
        </PageLayoutTop>
      )}
      <SectionTitle title={t('LABEL_SIDEBAR_ITEM_MY_COURSES_TITLE')} />

      <LazyMyCoursesWithFilters
        me={me}
        onCoursesChange={sendAmplitudeEventRenderRedesign}
        amplitudePageName={LEARN_DASHBOARD_PAGE_NAME}
      />
    </>
  );
};

export default CoursesSection;
